import { useId, useState } from "react";
import { Link } from "react-router";

import { EmptyState } from "../lib/EmptyState.tsx";
import { TileRow } from "../lib/Tiles.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { ARTIST_SETTLEMENT_COPY } from "./collector.constants.ts";
import type { ArtistSettlementRow } from "./artistSettlements.model.ts";
import { useArtistSettlementsController } from "./useArtistSettlementsController.ts";
import "../gallery/gallery.css";
import "../gallery/settlements.css";

/**
 * What a gallery's post-sale covenant owes you when your work is resold.
 *
 * THE OTHER SIDE OF THE GALLERY'S SCREEN, and narrower on purpose: no buyer, no
 * gallery split, no locked group. `ondo.artist_settlements()` returns none of
 * those — decision 3 of `20260906090000_resale_settlements.sql` discloses the
 * resale's buyer to the covenant HOLDER because a disputed settlement is
 * resolved by knowing the parties, and that argument does not reach a party who
 * is merely owed money out of it.
 *
 * IT EXISTS BECAUSE OF ONE CONTROL. ONDO moves no money, so a remittance is a
 * gallery asserting something about its own conduct toward a third party.
 * Everywhere else in this system a claim about somebody can be answered by
 * them; without this screen a settlement would be the only place a party is
 * recorded as PAID without ever being asked.
 *
 * It reuses the gallery's stylesheet rather than growing a second copy of the
 * same row. The two screens are the same object read from two sides.
 */
export function ArtistSettlements({ workBase }: { workBase: string }) {
  const { view, working, disputing, openDispute, closeDispute, confirm, dispute } =
    useArtistSettlementsController(workBase);

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="gallery__note gallery__note--error" role="alert">
        <h2 className="gallery__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <EmptyState className="gallery__empty" lead={view.message} detail={view.detail} />
    );
  }

  return (
    <>
      <p className="gallery__summary">{view.summary}</p>

      {/* OND-70: owed and recorded-as-paid, one pair of tiles per currency and
          never a sum across them. The model decides the figures; this draws. */}
      <section className="gallery__totals" aria-label={ARTIST_SETTLEMENT_COPY.totalsLabel}>
        <TileRow tiles={view.totals} />
      </section>

      <section className="gallery__lead">
        <p>
          <strong>{ARTIST_SETTLEMENT_COPY.lead}</strong>{" "}
          {ARTIST_SETTLEMENT_COPY.leadDetail}
        </p>
      </section>

      {/* WAITING ON YOU, first. The whole reason the screen exists. */}
      {view.toAnswer.length > 0 && (
        <Group title={ARTIST_SETTLEMENT_COPY.groups.answer} rows={view.toAnswer}>
          {(row) => (
            <Answer
              row={row}
              busy={working === row.settlementId}
              composing={disputing === row.settlementId}
              onConfirm={() => confirm(row.settlementId)}
              onOpenDispute={() => openDispute(row.settlementId)}
              onCancel={closeDispute}
              onDispute={(note) => dispute(row.settlementId, note)}
            />
          )}
        </Group>
      )}

      {view.owed.length > 0 && (
        <Group title={ARTIST_SETTLEMENT_COPY.groups.owed} rows={view.owed} />
      )}
      {view.closed.length > 0 && (
        <Group title={ARTIST_SETTLEMENT_COPY.groups.closed} rows={view.closed} />
      )}
    </>
  );
}

function Group({
  title,
  rows,
  children,
}: {
  title: string;
  rows: ArtistSettlementRow[];
  children?: (row: ArtistSettlementRow) => React.ReactNode;
}) {
  return (
    <section>
      <div className="gallery__group">
        <h2 className="gallery__group-title">
          {title} ({rows.length})
        </h2>
      </div>
      <ul className="gallery__settlements">
        {rows.map((row) => (
          <Row key={row.settlementId} row={row} action={children?.(row) ?? null} />
        ))}
      </ul>
    </section>
  );
}

function Row({
  row,
  action,
}: {
  row: ArtistSettlementRow;
  action: React.ReactNode;
}) {
  return (
    <li className={`gallery__settlement gallery__settlement--${row.state}`}>
      <Link className="gallery__settlement-open" to={row.href}>
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />
        <div className="gallery__settlement-body">
          <p className="gallery__settlement-title">
            {row.title}
            <span
              className={`gallery__settlement-state gallery__settlement-state--${row.state}`}
            >
              {row.stateLabel}
            </span>
          </p>
          <p className="gallery__settlement-meta">{row.resale}</p>
          {row.owedBy && (
            <p className="gallery__settlement-meta gallery__settlement-meta--soft">
              {row.owedBy}
            </p>
          )}
        </div>
        <div className="gallery__settlement-due">
          <span className="gallery__settlement-duelabel">
            {ARTIST_SETTLEMENT_COPY.owedLabel}
          </span>
          <span
            className={`gallery__settlement-dueval${
              row.owedToYou === ARTIST_SETTLEMENT_COPY.pending
                ? " gallery__settlement-dueval--pending"
                : ""
            }`}
          >
            {row.owedToYou}
          </span>
        </div>
      </Link>

      <div className="gallery__terms">
        <dl className="gallery__terms-figures">
          {row.figures.map((figure) => (
            <div key={figure.label}>
              <dt>{figure.label}</dt>
              <dd
                className={[
                  figure.negative ? "is-negative" : "",
                  figure.pending ? "is-pending" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {figure.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="gallery__settlement-foot">
        <p className="gallery__settlement-note">{row.note}</p>
        {action}
      </div>
    </li>
  );
}

/**
 * Confirm, or dispute with an optional reason.
 *
 * The reason is OPTIONAL on a dispute, deliberately. Requiring one would make
 * disagreeing the harder of the two acts, and this is the one control an artist
 * has over a figure somebody else chose.
 */
function Answer({
  row,
  busy,
  composing,
  onConfirm,
  onOpenDispute,
  onCancel,
  onDispute,
}: {
  row: ArtistSettlementRow;
  busy: boolean;
  composing: boolean;
  onConfirm: () => void;
  onOpenDispute: () => void;
  onCancel: () => void;
  onDispute: (note: string) => void;
}) {
  const fieldId = useId();
  const [note, setNote] = useState("");

  if (composing) {
    return (
      <form
        className="gallery__rate"
        onSubmit={(event) => {
          event.preventDefault();
          onDispute(note);
        }}
      >
        <label className="gallery__rate-label" htmlFor={fieldId}>
          {row.title}
        </label>
        <textarea
          id={fieldId}
          className="gallery__rate-input"
          rows={3}
          value={note}
          placeholder={ARTIST_SETTLEMENT_COPY.actions.notePlaceholder}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className="gallery__rate-actions">
          <button type="submit" className="gallery__settle" disabled={busy}>
            {ARTIST_SETTLEMENT_COPY.actions.submitDispute}
          </button>
          <button type="button" className="gallery__rate-cancel" onClick={onCancel}>
            {ARTIST_SETTLEMENT_COPY.actions.cancel}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="gallery__rate-actions">
      <button
        type="button"
        className="gallery__settle"
        disabled={busy}
        onClick={onConfirm}
      >
        {ARTIST_SETTLEMENT_COPY.actions.confirm}
      </button>
      <button
        type="button"
        className="gallery__lift"
        disabled={busy}
        onClick={onOpenDispute}
      >
        {ARTIST_SETTLEMENT_COPY.actions.dispute}
      </button>
    </div>
  );
}
